import { memo, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Alert, Anchor, CloseButton, List, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import { PartialMessage } from '@zg-rentals/backend-styled-components';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import accessUtils from '../../../utils/accessUtils';
import stringHelper from '../../../utils/stringHelper';

import { MEDIA_CREDITS_COOKIES } from '../../../constants/CookieConstants';
import { getListingRoute } from '../../../constants/urls';

import ArchivePropertyModal from '../../modals/ArchivePropertyModal';
import MultiToSingleFamilyConversionModal from '../../modals/MultitoSinglefamilyConversionModal';

import { useAuth } from '../../AuthProvider';
import MediaCreditsBanner from '../../MediaCreditsBanner';
import FeedListingSourceBanner from './FeedListingSourceBanner';

const Banners = (): JSX.Element => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const canFeedConnectSwitchSource = accessUtils.canFeedConnectSwitchSource({ user });

  const [displayArchivePropertyModal, setDisplayArchivePropertyModal] = useState(false);
  const [showMultiToSingleFamilyConversionModal, setShowMultiToSingleFamilyConversionModal] = useState(false);

  const isConversionRequired = listingDetails?.multiToSingleFamilyConversion?.isConversionRequired;
  const isSelfServeOnboardingInProgress = listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress;
  const convertedFromListingAlias = listingDetails?.multiToSingleFamilyConversion?.convertedFromListingAlias;
  const partialFeedName = listingDetails?.partialListing?.partialFeedName;
  const customerProblems = listingDetails?.syndication?.customerProblems;
  const universalSyndicationMessages = listingDetails?.syndication?.universal?.partialMessages;

  const initialShowConversionModal = router.query.convertToSingleFamilyListing === 'true';

  useEffect(() => {
    setShowMultiToSingleFamilyConversionModal(initialShowConversionModal);
  }, [initialShowConversionModal]);

  const handleConvertClick = () => {
    setShowMultiToSingleFamilyConversionModal(true);
  };

  return (
    <>
      {Boolean(listingDetails?.isArchived && (!isConversionRequired || isSelfServeOnboardingInProgress)) && (
        <Alert
          actionButton={<TextButton onClick={() => setDisplayArchivePropertyModal(true)}>Unarchive</TextButton>}
          css={{ marginBottom: '600' }}
        >
          This property is archived. Unarchive it to make edits.
        </Alert>
      )}
      {Boolean(
        !listingDetails?.active &&
        !listingDetails?.updateActiveStatus?.isAllowed &&
        listingDetails?.updateActiveStatus?.partialMessages &&
        listingDetails?.isMultifamily &&
        !isSelfServeOnboardingInProgress,
      ) && (
        <Alert
          tone="warning"
          actionButton={isConversionRequired && <TextButton onClick={handleConvertClick}>Convert</TextButton>}
          closeButton={<CloseButton />}
          css={{ marginBottom: '600' }}
          data-testid="RestrictionsBanner"
        >
          <PartialMessage partialMessages={listingDetails?.updateActiveStatus?.partialMessages ?? []} />
        </Alert>
      )}
      {Boolean(convertedFromListingAlias) && (
        <Alert tone="info" css={{ marginBottom: '600' }}>
          <Text>
            View the previous version of this&nbsp;
            <Anchor asChild>
              <Link
                href={getListingRoute({
                  listingAlias: convertedFromListingAlias ?? '',
                })}
              >
                listing
              </Link>
            </Anchor>
            .
          </Text>
        </Alert>
      )}
      {Boolean(!listingDetails?.isFeed && listingDetails?.partialListing?.isPartialListingManaged) && (
        <Alert tone="info" css={{ marginBottom: '600' }}>
          <Text>
            {partialFeedName} is the pricing and availability source for this listing. Edits to property details and
            media can be made on Zillow, but edits to availability and pricing must be made through {partialFeedName}.
          </Text>
        </Alert>
      )}
      {Boolean(canFeedConnectSwitchSource) && listingDetails?.feedId !== 'StreetEasy' && <FeedListingSourceBanner />}
      {Boolean(customerProblems?.length && customerProblems.length > 0) && (
        <Alert
          tone="warning"
          closeButton={<CloseButton />}
          css={{ marginBottom: '600' }}
          data-test="CustomerListingProblemsBanner"
        >
          <Paragraph textStyle="body-bold" css={{ marginBottom: '200' }}>
            {stringHelper.pluralize(customerProblems?.length ?? 0, 'issue has', 'issues have')}
            &nbsp;been identified on this listing:
          </Paragraph>
          <List.Root>
            {customerProblems?.map((problem) => {
              return (
                <List.Item key={`CustomerProblems-${problem}`} css={{ paddingBlock: '0' }}>
                  {problem}
                </List.Item>
              );
            })}
          </List.Root>
        </Alert>
      )}
      {Boolean(universalSyndicationMessages?.length && universalSyndicationMessages.length > 0) && (
        <Alert tone="info" css={{ marginBottom: '600' }}>
          <PartialMessage partialMessages={universalSyndicationMessages ?? []} />
        </Alert>
      )}

      <MediaCreditsBanner cookieKey={MEDIA_CREDITS_COOKIES.HAS_CLOSED_PAGE_BANNER} tone="hero" />

      {displayArchivePropertyModal && (
        <ArchivePropertyModal
          onClose={() => setDisplayArchivePropertyModal(false)}
          isArchived={listingDetails?.isArchived ?? false}
          listingAlias={listingAlias}
          address={{
            street: listingDetails?.street ?? '',
            city: listingDetails?.city ?? '',
            state: listingDetails?.state ?? '',
            zip: listingDetails?.zip ?? '',
            unit: listingDetails?.unit ?? '',
          }}
        />
      )}
      {showMultiToSingleFamilyConversionModal && isConversionRequired && (
        <MultiToSingleFamilyConversionModal
          setShowMultiToSingleFamilyConversionModal={setShowMultiToSingleFamilyConversionModal}
        />
      )}
    </>
  );
};

export default memo(Banners);
